import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Container, alpha, Grid, Chip } from '@mui/material';
import {
  ShoppingCart as EcommerceIcon,
  Restaurant as RestaurantIcon,
  Code as SaaSIcon,
  Storefront as RetailIcon,
  BusinessCenter as ServicesIcon,
  LocalHospital as HealthcareIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';

const IndustrySection: React.FC = () => {
  const industries = [
    {
      icon: <EcommerceIcon />,
      name: 'E-commerce',
      kpis: ['Revenue and transactions', 'Average order value', 'Sixty-day revenue projection'],
      gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
      color: '#6366F1',
    },
    {
      icon: <RestaurantIcon />,
      name: 'Restaurant',
      kpis: ['Comps and voids by employee', 'Average check by day part', 'Revenue and transactions'],
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)',
      color: '#F59E0B',
    },
    {
      icon: <SaaSIcon />,
      name: 'SaaS',
      kpis: ['Revenue and transactions', 'Sixty-day revenue projection'],
      gradient: 'linear-gradient(135deg, #10B981 0%, #06D6A0 100%)',
      color: '#10B981',
    },
    {
      icon: <RetailIcon />,
      name: 'Retail',
      kpis: ['Revenue and transactions', 'Average basket size', 'Sixty-day revenue projection'],
      gradient: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
      color: '#EC4899',
    },
    {
      icon: <ServicesIcon />,
      name: 'Professional Services',
      kpis: ['Revenue and transactions', 'Sixty-day revenue projection'],
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
      color: '#3B82F6',
    },
    {
      icon: <HealthcareIcon />,
      name: 'Healthcare',
      kpis: ['Revenue and transactions', 'Average payment size', 'Sixty-day revenue projection'],
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
      color: '#8B5CF6',
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.gradient,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(tokens.secondary.main, 0.08)} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0.15, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                px: 2,
                py: 0.75,
                borderRadius: '100px',
                background: alpha(tokens.secondary.main, 0.08),
                border: `1px solid ${alpha(tokens.secondary.main, 0.15)}`,
              }}
            >
              <SparkleIcon sx={{ fontSize: 16, color: tokens.secondary.main }} />
              <Typography
                variant="caption"
                sx={{ color: tokens.secondary.main, fontWeight: 600 }}
              >
                Industry-Specific
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: tokens.text.primary,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
              }}
            >
              Built for{' '}
              <Box
                component="span"
                sx={{
                  background: tokens.secondary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Your Industry
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: tokens.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              We guess your industry from your website and use it to pick which KPIs you start with. It is keyword matching on your site content, and you can change what it picked.
            </Typography>
          </Box>
        </motion.div>

        {/* Industries Grid */}
        <Grid container spacing={3}>
          {industries.map((industry, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0.15, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: '20px',
                    background: tokens.background.paper,
                    border: `1px solid ${tokens.border.light}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      borderColor: alpha(industry.color, 0.4),
                      boxShadow: `0 20px 50px ${alpha(industry.color, 0.2)}`,
                      '& .industry-icon': {
                        transform: 'scale(1.15)',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      className="industry-icon"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        background: industry.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: `0 8px 20px ${alpha(industry.color, 0.35)}`,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {React.cloneElement(industry.icon, { sx: { fontSize: 28 } })}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: tokens.text.primary,
                      }}
                    >
                      {industry.name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {industry.kpis.map((kpi, kpiIndex) => (
                      <Chip
                        key={kpiIndex}
                        label={kpi}
                        size="small"
                        sx={{
                          background: alpha(industry.color, 0.1),
                          color: industry.color,
                          fontWeight: 500,
                          border: `1px solid ${alpha(industry.color, 0.2)}`,
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Auto-detect Banner */}
        <motion.div
          initial={{ opacity: 0.15, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box
            sx={{
              mt: 6,
              p: 3,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${alpha(tokens.primary.main, 0.08)} 0%, ${alpha(tokens.secondary.main, 0.08)} 100%)`,
              border: `1px solid ${alpha(tokens.primary.main, 0.15)}`,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: tokens.text.primary,
                fontWeight: 500,
              }}
            >
              <Box
                component="span"
                sx={{
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 700,
                }}
              >
                Don't see your industry?
              </Box>
              {' '}The dashboards are the same whatever you sell. Nothing about them is tuned to an industry.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default IndustrySection;
